/**
 * "Before you can lodge" — the blockers band (Specs/submission-pack-spec.md,
 * Task B6; reshaped by Specs/2026-09-23-submission-pack-redesign-plan.md, Task 3.2).
 *
 * Always the first thing under the header and never collapsed while non-empty:
 * this is the one place that answers "what stops us submitting today?". Flat by
 * the design system's rules — no card, no box — with a wrapping row of chips.
 *
 * Every chip leads somewhere:
 *
 * - Off-page (compliance matrix) → a real `<Link>`, so it is middle-clickable
 *   and copyable.
 * - List lenses ("items still to do", "over the word limit") → the host
 *   applies a filter, so the chip shows exactly the rows its number counts.
 * - "N waiting on the team" → the host opens the "Waiting on the team" section.
 * - Removed by addendum → scrolls to the strip.
 *
 * `openTodos` is not a chip: it counts the same markers as the questions chip
 * (raw rather than deduped). It stays in `packBlockerLabels` for the export
 * dialog, which must warn that every marker lands in the export.
 */

import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowUpRight, CheckCircle2 } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { complianceMatrixLink } from '@/lib/compliance-position'
import { cn } from '@/lib/utils'
import type { PackItem, SubmissionPackBlockers } from '@/lib/api'
import { PACK_PREPARATION_COPY } from '@/lib/pack-readiness'

/** The strip's anchor in `PackList`. Also the only way to reach a strip row. */
export const REMOVED_BY_ADDENDUM_ANCHOR = 'removed-by-addendum'

/** The band's own anchor — the export dialog's "Review blockers" scrolls here. */
export const PACK_BLOCKERS_ANCHOR = 'pack-blockers'

type BlockerKey = keyof SubmissionPackBlockers

/**
 * The blocker wording, in display order, for every non-zero count.
 *
 * Exported because the export dialog lists the same blockers before it lets the
 * pack out of the door: two hand-written copies of "3 responses are over the
 * word limit" would drift the first time one of them is reworded.
 */
export function packBlockerLabels(
  blockers: SubmissionPackBlockers
): { key: BlockerKey; label: string }[] {
  const labels: { key: BlockerKey; label: string }[] = []

  if (blockers.mandatoryAtRisk > 0) {
    labels.push({
      key: 'mandatoryAtRisk',
      label: `${blockers.mandatoryAtRisk} mandatory ${
        blockers.mandatoryAtRisk === 1 ? 'requirement' : 'requirements'
      } at risk`,
    })
  }
  if (blockers.missingHandovers > 0) {
    labels.push({
      key: 'missingHandovers',
      label: PACK_PREPARATION_COPY.handoversNotPrepared(blockers.missingHandovers),
    })
  }
  if (blockers.undraftedResponses > 0) {
    labels.push({
      key: 'undraftedResponses',
      label: `${blockers.undraftedResponses} ${
        blockers.undraftedResponses === 1 ? 'response' : 'responses'
      } not drafted`,
    })
  }
  if (blockers.unacknowledged > 0) {
    labels.push({
      key: 'unacknowledged',
      label: `${blockers.unacknowledged} ${
        blockers.unacknowledged === 1 ? 'acknowledgement' : 'acknowledgements'
      } not ticked`,
    })
  }
  if (blockers.overWordLimit > 0) {
    labels.push({
      key: 'overWordLimit',
      label: `${blockers.overWordLimit} ${
        blockers.overWordLimit === 1 ? 'response is' : 'responses are'
      } over the word limit`,
    })
  }
  if (blockers.openTodos > 0) {
    labels.push({
      key: 'openTodos',
      // Worded apart from "Waiting on the team · N" on purpose. This is the RAW
      // marker count: answering a marker takes its ask off that list while the
      // `[TODO: …]` token is still in the draft and still heading for the
      // export. The two numbers legitimately differ, so they must not read as
      // the same sentence.
      label: `${blockers.openTodos} unresolved TODO ${
        blockers.openTodos === 1 ? 'marker' : 'markers'
      } in drafts`,
    })
  }
  if (blockers.removedByAddendum > 0) {
    labels.push({
      key: 'removedByAddendum',
      label: `${blockers.removedByAddendum} ${
        blockers.removedByAddendum === 1 ? 'item' : 'items'
      } removed by addendum`,
    })
  }

  return labels
}

interface PackBlockersProps {
  bidId: string
  blockers: SubmissionPackBlockers
  removedByAddendum?: PackItem[]
  /**
   * Distinct questions in "Waiting on the team" (deduped server-side). Drives
   * the "N waiting on the team" chip.
   */
  needsCount?: number
  /**
   * Renders nothing at all instead of the green all-clear line. The pack page
   * sets it on an empty checklist: "Nothing left to prepare" over a
   * pack with no items is a false all-clear, not a reassurance. It suppresses
   * ONLY the clear state — a non-zero blocker always renders, because
   * `mandatoryAtRisk` and `openTodos` are counted independently of the
   * checklist and can be non-zero while it is empty (spec P2.2).
   */
  hideWhenClear?: boolean
  /** "N items still to do" — the host filters the list to To do. */
  onShowToDo?: () => void
  /** "N responses over the word limit" — the host applies the over-limit lens. */
  onShowOverLimit?: () => void
  /** "N waiting on the team" — the host opens the Waiting on the team section. */
  onShowNeeds?: () => void
}

// ===================
// Helpers
// ===================

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

function scrollToSelector(selector: string) {
  document.querySelector(selector)?.scrollIntoView({ block: 'center' })
}

function scrollToRow(itemId: string) {
  scrollToSelector(`[data-row-id="${CSS.escape(itemId)}"]`)
}

// ===================
// Component
// ===================

type Chip =
  | { key: string; label: string; href: string }
  | { key: string; label: string; onSelect?: (() => void) | undefined }

const CHIP_CLASS = cn(
  buttonVariants({ variant: 'outline' }),
  'h-8 gap-2 px-3 text-sm'
)

function WarningDot() {
  return (
    <span
      aria-hidden="true"
      className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--status-warning))]"
    />
  )
}

export function PackBlockers({
  bidId,
  blockers,
  removedByAddendum = [],
  needsCount = 0,
  hideWhenClear = false,
  onShowToDo,
  onShowOverLimit,
  onShowNeeds,
}: PackBlockersProps) {
  const chips: Chip[] = []

  const stillToDo =
    blockers.missingHandovers + blockers.undraftedResponses + blockers.unacknowledged
  if (stillToDo > 0) {
    chips.push({
      key: 'stillToDo',
      label: `${plural(stillToDo, 'item', 'items')} still to do`,
      onSelect: onShowToDo,
    })
  }
  if (blockers.overWordLimit > 0) {
    chips.push({
      key: 'overWordLimit',
      label: `${plural(blockers.overWordLimit, 'response', 'responses')} over the word limit`,
      onSelect: onShowOverLimit,
    })
  }
  if (blockers.mandatoryAtRisk > 0) {
    chips.push({
      key: 'mandatoryAtRisk',
      label: `${blockers.mandatoryAtRisk} mandatory ${
        blockers.mandatoryAtRisk === 1 ? 'requirement' : 'requirements'
      } at risk`,
      // The `at_risk` pseudo-value expansion lives behind this helper — never
      // hand-build the matrix URL, or the link stops matching the count.
      href: complianceMatrixLink(bidId, { position: 'at_risk', criticality: 'mandatory' }),
    })
  }
  if (needsCount > 0) {
    chips.push({
      key: 'needs',
      label: `${needsCount} waiting on the team`,
      onSelect: onShowNeeds,
    })
  } else if (blockers.openTodos > 0) {
    // Every question answered and resolved, but the `[TODO: …]` tokens are
    // still in the drafts and still heading for the export. Nothing on this
    // page resolves that, so it is stated rather than linked — the band must
    // not read as all-clear while the export dialog would warn.
    chips.push({
      key: 'openTodos',
      label: `${plural(blockers.openTodos, 'unresolved TODO marker', 'unresolved TODO markers')} in drafts`,
    })
  }
  if (blockers.removedByAddendum > 0) {
    const target = removedByAddendum[0]
    chips.push({
      key: 'removedByAddendum',
      label: `${plural(blockers.removedByAddendum, 'item', 'items')} removed by addendum`,
      // The strip's own rows are unreachable by `?item=`, so the anchor is the
      // target; the first row is a nicety when it happens to be rendered.
      onSelect: () =>
        target ? scrollToRow(target.id) : scrollToSelector(`#${REMOVED_BY_ADDENDUM_ANCHOR}`),
    })
  }

  if (chips.length === 0) {
    if (hideWhenClear) return null
    return (
      <section
        id={PACK_BLOCKERS_ANCHOR}
        aria-label="Before you can lodge"
        className="flex items-center gap-2"
      >
        <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--status-success))]" />
        {/* R0-B: an all-clear over preparation only — never "ready to submit". */}
        <p className="text-sm text-[hsl(var(--status-success))]">
          {PACK_PREPARATION_COPY.allClear}
        </p>
      </section>
    )
  }

  return (
    <section id={PACK_BLOCKERS_ANCHOR} aria-label="Before you can lodge">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--status-warning))]" />
        <h3 className="text-sm font-medium text-foreground">Before you can lodge</h3>
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {chips.map((chip) => (
          <li key={chip.key}>
            {'href' in chip ? (
              <Link to={chip.href} className={CHIP_CLASS}>
                <WarningDot />
                {chip.label}
                <ArrowUpRight aria-hidden className="h-4 w-4 text-subtle" />
              </Link>
            ) : chip.onSelect ? (
              <button type="button" onClick={chip.onSelect} className={CHIP_CLASS}>
                <WarningDot />
                {chip.label}
              </button>
            ) : (
              <span className="inline-flex h-8 items-center gap-2 rounded-lg border border-border px-3 text-sm text-foreground">
                <WarningDot />
                {chip.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
